/**
 * A spotlight tour: each step dims the page and lifts ONE thing out of it,
 * with a card that says what it is for in a sentence or two. First built for
 * the KPI dashboard (DOP-609); every workflow page uses it now, each with its
 * own steps.
 *
 * Anchors are found by selector rather than by ref: a tab lives in the
 * shell's tab strip, outside the component that owns the tour, and a selector
 * reaches it without threading refs through the shell. A step whose anchor is
 * not on screen is skipped rather than drawn pointing at nothing.
 *
 * The spotlight is a transparent box over the anchor whose enormous outer
 * shadow is the dimmer, so nothing has to be re-stacked above an overlay. It
 * is portalled to <body> so no ancestor's transform can move it.
 *
 * Modal while open, for the keyboard too: Tab cycles inside the card, and
 * focus goes back to wherever it was when the tour closes.
 */

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface TourStep {
	/** A selector for the one thing this step lifts out of the page. */
	anchor: string;
	title: string;
	body: string;
	note?: string;
	/** The primary button's words: "Next: …", or "Got it" on the last step. */
	next: string;
}

const CARD_WIDTH = 400;
/** Room the card needs under its anchor before it flips above it instead. */
const CARD_HEIGHT_GUESS = 250;
const EDGE = 16;
const PAD = 4;
/** A tall anchor (the grid) gets the card laid over its top, not under it. */
const TALL_ANCHOR = 280;

export interface SpotlightTourProps {
	steps: readonly TourStep[];
	onClose: () => void;
	/** The backdrop's test id, so a page's tests can tell its tour apart. */
	testId?: string;
}

const SpotlightTour: React.FC<SpotlightTourProps> = ({
	steps: STEPS,
	onClose,
	testId = "tour-backdrop",
}) => {
	const titleId = useId();
	const [index, setIndex] = useState(0);
	const [rect, setRect] = useState<DOMRect | null>(null);
	const primaryRef = useRef<HTMLButtonElement>(null);
	const cardRef = useRef<HTMLElement>(null);

	// Return focus to what had it before the tour — "Take the tour", or the
	// page — rather than dropping it on <body> when the card unmounts.
	useEffect(() => {
		const previous = document.activeElement as HTMLElement | null;
		return () => previous?.focus?.();
	}, []);
	const step = STEPS[index];

	// Only a real move re-renders: the frame loop below calls this 60 times a
	// second, and a new DOMRect every time would re-render the tour as often.
	const measure = useCallback(() => {
		const anchor = document.querySelector(step.anchor);
		const next = anchor ? anchor.getBoundingClientRect() : null;
		setRect((prev) =>
			prev !== null &&
			next !== null &&
			prev.left === next.left &&
			prev.top === next.top &&
			prev.width === next.width &&
			prev.height === next.height
				? prev
				: next,
		);
	}, [step.anchor]);

	// Measured before paint so the card never flashes at the previous step's
	// position.
	useLayoutEffect(() => {
		const anchor = document.querySelector(step.anchor);
		if (!anchor) {
			// Nothing to point at (the key was closed, the tab is missing):
			// move on rather than spotlight empty space.
			if (index < STEPS.length - 1) setIndex(index + 1);
			else onClose();
			return;
		}
		anchor.scrollIntoView?.({ block: "nearest" });
		measure();
	}, [index, step.anchor, measure, onClose, STEPS.length]);

	useEffect(() => {
		// Follow the anchor every frame, not just on scroll and resize: the page
		// keeps laying itself out under an open tour (a tab's count arrives, the
		// tiles fill in, a poll adds a row, a smooth scroll is still moving),
		// and a spotlight measured once is left pointing where the thing was.
		let frame = 0;
		const follow = () => {
			measure();
			frame = window.requestAnimationFrame(follow);
		};
		frame = window.requestAnimationFrame(follow);
		return () => window.cancelAnimationFrame(frame);
	}, [measure]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
				return;
			}
			if (event.key !== "Tab" || !cardRef.current) return;
			// Keep Tab inside the card: the page behind it is dimmed and, for
			// as long as the card says aria-modal, not the reader's to reach.
			const focusable = Array.from(
				cardRef.current.querySelectorAll<HTMLElement>("button:not([disabled])"),
			);
			if (focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement as HTMLElement | null;
			const inside = active !== null && cardRef.current.contains(active);
			if (event.shiftKey && (!inside || active === first)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && (!inside || active === last)) {
				event.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [onClose]);

	// Focus follows the step, once its card exists.
	const shown = rect !== null;
	useEffect(() => {
		if (shown) primaryRef.current?.focus();
	}, [index, shown]);

	if (!rect) return null;

	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;
	const tall = rect.height > TALL_ANCHOR;
	const below = tall || rect.bottom + CARD_HEIGHT_GUESS < viewportHeight;
	const top = tall
		? rect.top + 48
		: below
			? rect.bottom + 14
			: Math.max(EDGE, rect.top - CARD_HEIGHT_GUESS - 14);
	const left = Math.max(
		EDGE,
		Math.min(rect.left + (tall ? 240 : 40), viewportWidth - CARD_WIDTH - EDGE),
	);
	const arrowLeft = Math.max(
		16,
		Math.min(rect.left + Math.min(rect.width / 2, 60) - left, CARD_WIDTH - 32),
	);
	const last = index === STEPS.length - 1;

	// Portalled to <body>: the spotlight and card are `position: fixed` and
	// placed from viewport coordinates, and an ancestor with a transform, a
	// filter or `will-change` (a page's enter animation) would otherwise
	// become their frame and shift and stretch them off the thing they point at.
	return createPortal(
		<>
			{/* Modal for the pointer too: a clear layer over the whole page
			    takes clicks meant for what is behind the dimmer (Run now, the
			    tabs, the grid), which could otherwise navigate or unmount the
			    tour mid-step. */}
			<div
				aria-hidden="true"
				data-testid={testId}
				className="fixed inset-0 z-[59]"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none fixed z-[60] rounded-xl ring-[3px] ring-[#008782]"
				style={{
					left: rect.left - PAD,
					top: rect.top - PAD,
					width: rect.width + PAD * 2,
					height: rect.height + PAD * 2,
					boxShadow: "0 0 0 4px #ffffff, 0 0 0 9999px rgba(17,24,39,0.5)",
				}}
			/>
			<section
				ref={cardRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				className="fixed z-[61] flex flex-col gap-2.5 rounded-[14px] bg-white px-[22px] py-5 shadow-[0_24px_64px_rgba(17,24,39,0.35)]"
				style={{ left, top, width: CARD_WIDTH }}
			>
				{below && !tall && (
					<div
						aria-hidden="true"
						className="absolute -top-[7px] h-3.5 w-3.5 rotate-45 bg-white"
						style={{ left: arrowLeft }}
					/>
				)}
				<span className="text-[11px] font-semibold uppercase tracking-[.09em] text-[#008782]">
					Quick tour · {index + 1} of {STEPS.length}
				</span>
				<h2 id={titleId} className="text-lg font-semibold text-[#111111]">
					{step.title}
				</h2>
				<p className="text-sm leading-[1.55] text-[#3f4247]">{step.body}</p>
				{step.note && (
					<p className="text-[13px] leading-normal text-text-muted">{step.note}</p>
				)}
				<div className="mt-1.5 flex items-center gap-2.5">
					<span aria-hidden="true" className="flex gap-[5px]">
						{STEPS.map((candidate, dot) => (
							<span
								key={candidate.anchor}
								className={`h-1.5 rounded-full ${
									dot === index ? "w-[18px] bg-[#004C4B]" : "w-1.5 bg-[#d5d8dc]"
								}`}
							/>
						))}
					</span>
					{!last && (
						<button
							type="button"
							onClick={onClose}
							className="ml-auto text-[13px] font-medium text-text-muted hover:text-text-base"
						>
							Skip tour
						</button>
					)}
					<button
						ref={primaryRef}
						type="button"
						onClick={() => (last ? onClose() : setIndex(index + 1))}
						className={`${last ? "ml-auto" : ""} rounded-lg bg-[#004C4B] px-3.5 py-[9px] text-[13px] font-semibold text-white hover:bg-[#003a3a] focus:outline-none focus:ring-2 focus:ring-[#008782] focus:ring-offset-2`}
					>
						{step.next}
					</button>
				</div>
			</section>
		</>,
		document.body,
	);
};

export default SpotlightTour;
